import axios from 'axios';

/*
 * Home screen ("Add to Home Screen" / install) identity for a client page.
 *
 * Phones read the app name, icon and start URL from the page that is open when the user adds it.
 * Those values used to be hard-coded ("TAG", the TAG logo, start_url "/"), so every client page
 * installed as "TAG" and opened the bare root domain. Once the client is known we replace them with:
 *   - name:       "SaySomething"
 *   - icon:       the client's logo, drawn into square PNGs (180 for iOS, 192/512 for Android)
 *   - start URL:  the client's own page (and scope)
 * If anything fails (no logo, logo not readable) we keep the default TAG assets, never nothing.
 */

export const APP_NAME = 'SaySomething';

const USING_SUBDOMAIN = process.env.REACT_APP_USING_SUBDOMAIN === 'true';
const ICON_BACKGROUND = '#ffffff';
const ICON_PADDING = 0.1; // keep the logo clear of the rounded corners
const IMAGE_TIMEOUT_MS = 8000;

function upsertLink(rel, attributes) {
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  Object.entries(attributes).forEach(([key, value]) =>
    link.setAttribute(key, value)
  );
}

function upsertMeta(name, content) {
  let meta = document.head.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function loadImage(url, { crossOrigin } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(
      () => reject(new Error('logo timeout')),
      IMAGE_TIMEOUT_MS
    );
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('logo failed to load'));
    };
    img.src = url;
  });
}

// Draws the logo, centred and scaled to fit, onto a square white PNG.
function toSquareIcon(img, size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = ICON_BACKGROUND;
  ctx.fillRect(0, 0, size, size);
  const box = size * (1 - ICON_PADDING * 2);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  const scale = Math.min(box / width, box / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;
  ctx.drawImage(
    img,
    (size - drawWidth) / 2,
    (size - drawHeight) / 2,
    drawWidth,
    drawHeight
  );
  return canvas.toDataURL('image/png'); // throws if the image was not CORS-readable
}

function absoluteLogoUrl(logo) {
  if (!logo) return null;
  if (logo.startsWith('http')) return logo;
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  return `${base}/${logo.replace(/^\//, '')}`;
}

// Looks up the client's logo URL. Returns null when the client or logo is unknown.
export async function fetchClientLogoUrl(orgKey) {
  const serverUrl = process.env.REACT_APP_BACKEND_URL;
  const res = await axios.get(
    `${serverUrl}organizations?sub_domain=${encodeURIComponent(orgKey)}`
  );
  const org = (res?.data ?? []).find((o) => o.sub_domain === orgKey);
  return absoluteLogoUrl(org && org.logo);
}

// Icons built from the logo: { 180, 192, 512 } data URLs, or the raw logo URL if the browser will
// not let us read its pixels, or null if the logo cannot be loaded at all.
async function buildIcons(logoUrl) {
  if (!logoUrl) return null;
  try {
    const img = await loadImage(logoUrl, { crossOrigin: 'anonymous' });
    return {
      180: toSquareIcon(img, 180),
      192: toSquareIcon(img, 192),
      512: toSquareIcon(img, 512)
    };
  } catch (e) {
    try {
      await loadImage(logoUrl); // loads without CORS: usable as an <img>/icon URL, just not resizable
      return { raw: logoUrl };
    } catch (e2) {
      return null;
    }
  }
}

function defaultManifestIcons(origin) {
  return [
    { src: `${origin}/Logo192x192.png`, sizes: '192x192', type: 'image/png' },
    { src: `${origin}/Logo512.png`, sizes: '512x512', type: 'image/png' }
  ];
}

// Only Chromium-based browsers (Chrome, Edge, Samsung Internet) pick up a manifest that is added after the
// page has loaded. Firefox and every iPhone browser read the manifest when the page loads, so they get no
// manifest and make a shortcut to the page that is open instead (which is the client's own page).
function supportsLateManifest() {
  const ua = navigator.userAgent || '';
  return /Chrome\//.test(ua) && !/(Firefox|FxiOS|CriOS|EdgiOS|OPiOS)/.test(ua);
}

export async function applyClientIdentity({ orgKey, logoUrl }) {
  const { origin } = window.location;
  const startUrl = USING_SUBDOMAIN
    ? `${origin}/`
    : `${origin}/${encodeURIComponent(orgKey)}/`;

  document.title = APP_NAME;
  upsertMeta('apple-mobile-web-app-title', APP_NAME);
  upsertMeta('application-name', APP_NAME);

  const icons = await buildIcons(logoUrl);

  let manifestIcons = defaultManifestIcons(origin);
  upsertLink('apple-touch-icon', { href: `${origin}/apple-touch-icon.png` });
  if (icons && icons[180]) {
    upsertLink('apple-touch-icon', { href: icons[180], sizes: '180x180' });
    manifestIcons = [
      { src: icons[192], sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: icons[512], sizes: '512x512', type: 'image/png', purpose: 'any' }
    ];
  } else if (icons && icons.raw) {
    upsertLink('apple-touch-icon', { href: icons.raw });
    manifestIcons = [{ src: icons.raw, sizes: '192x192 512x512' }];
  }

  const manifest = {
    name: APP_NAME,
    short_name: APP_NAME,
    start_url: startUrl,
    scope: startUrl,
    display: 'standalone',
    theme_color: '#000000',
    background_color: '#ffffff',
    icons: manifestIcons
  };
  // Firefox on Android makes its shortcut icon from the page's regular icon link, so point that at the logo too.
  if (icons && icons[192]) {
    upsertLink('icon', {
      href: icons[192],
      type: 'image/png',
      sizes: '192x192'
    });
  } else if (icons && icons.raw) {
    upsertLink('icon', { href: icons.raw });
  }

  if (!supportsLateManifest()) {
    const existing = document.head.querySelector('link[rel="manifest"]');
    if (existing) existing.remove();
    return;
  }
  const blob = new Blob([JSON.stringify(manifest)], {
    type: 'application/manifest+json'
  });
  upsertLink('manifest', { href: URL.createObjectURL(blob) });
}
